import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
	FreeArea,
	OrientationType,
	PlatformType,
} from "@rubyplay/effw-connector";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { useSnapshotBridge } from "@/contexts/SnapshotBridgeContext";
import type { DockSurfaceState } from "@/surfaces/SurfaceTypes";
import { HitAreaWidget } from "@/components/HitAreaWidget";
import { debugLog, debugWarn } from "@/utils";
import { useSubscription } from "@/hooks/useSubscription";
import type {
	EngagementProvider,
	EngagementSnapshotWidgetId,
} from "@/provider/EngagementProvider";
import type { EFFeatureId } from "@/features/runtime/FeatureTypes";

const OFFSCREEN_PARK_STYLE: React.CSSProperties = {
	position: "fixed",
	left: -100000,
	top: -100000,
	width: 1,
	height: 1,
	overflow: "hidden",
	pointerEvents: "none",
	opacity: 0,
};

const CAPTAINUP_BASE_SIZE = 120;

export type WidgetDockProps = {
	freeAreas: readonly Readonly<FreeArea>[];
	platform: PlatformType;
	orientation: OrientationType;
	children: React.ReactNode;

	onDockStateChange?: (state: DockSurfaceState) => void;

	hitAreaName?: string;
	onActivate?: () => void;

	hitAreaAreaIds?: string[];

	provider: EngagementProvider | null;
	activeFeatureId: EFFeatureId | null;
	activeSnapshotWidgetId: EngagementSnapshotWidgetId | null;
	activeDockHasContent: boolean;
};

function resolveSelectedArea(
	freeAreas: readonly Readonly<FreeArea>[],
	platform: PlatformType,
	orientation: OrientationType
): Readonly<FreeArea> | null {
	if (freeAreas.length === 0) {
		debugLog("[WidgetDock] resolveSelectedArea() -> no freeAreas received", {
			platform,
			orientation,
		});
		return null;
	}

	const resolvedAreas = freeAreas.filter((area) => {
		const x = area.absolute?.x;
		const y = area.absolute?.y;

		return (
			typeof x === "number" &&
			Number.isFinite(x) &&
			typeof y === "number" &&
			Number.isFinite(y) &&
			typeof area.width === "number" &&
			Number.isFinite(area.width) &&
			typeof area.height === "number" &&
			Number.isFinite(area.height)
		);
	});

	if (resolvedAreas.length === 0) {
		debugLog("[WidgetDock] resolveSelectedArea() -> no measured freeAreas received", {
			platform,
			orientation,
			receivedAreaIds: freeAreas.map((area) => area.id),
		});
		return null;
	}

	if (resolvedAreas.length === 1) {
		const singleArea = resolvedAreas[0];

		debugLog(
			"[WidgetDock] resolveSelectedArea() -> using single authoritative freeArea from host",
			{
				platform,
				orientation,
				areaId: singleArea.id,
				areaPlatform: singleArea.platform,
				areaOrientation: singleArea.orientation,
				tooltipPosition: singleArea.tooltipPosition,
				x: singleArea.absolute?.x,
				y: singleArea.absolute?.y,
				width: singleArea.width,
				height: singleArea.height,
			}
		);

		return singleArea;
	}

	const matchingAreas = resolvedAreas.filter(
		(area) => area.platform === platform && area.orientation === orientation
	);

	if (matchingAreas.length === 0) {
		debugWarn(
			"[WidgetDock] resolveSelectedArea() -> no matching freeArea for platform+orientation",
			{
				platform,
				orientation,
				receivedAreaIds: freeAreas.map((area) => area.id),
				receivedAreas: freeAreas.map((area) => ({
					id: area.id,
					platform: area.platform,
					orientation: area.orientation,
					tooltipPosition: area.tooltipPosition,
					x: area.absolute?.x,
					y: area.absolute?.y,
					width: area.width,
					height: area.height,
				})),
			}
		);
		return null;
	}

	if (matchingAreas.length > 1) {
		debugWarn(
			"[WidgetDock] resolveSelectedArea() -> multiple matching freeAreas, using first one",
			{
				platform,
				orientation,
				matchingAreaIds: matchingAreas.map((area) => area.id),
			}
		);
	}

	const selectedArea = matchingAreas[0];

	debugLog("[WidgetDock] resolveSelectedArea() -> selected by platform+orientation", {
		platform,
		orientation,
		areaId: selectedArea.id,
		x: selectedArea.absolute?.x,
		y: selectedArea.absolute?.y,
		width: selectedArea.width,
		height: selectedArea.height,
		tooltipPosition: selectedArea.tooltipPosition,
	});

	return selectedArea;
}

function buildSnapshotStubMessage(
	activeSnapshotWidgetId: EngagementSnapshotWidgetId | null
): string {
	if (!activeSnapshotWidgetId) {
		return "[WidgetDock] HitAreaWidget snapshot forwarding is stubbed because the active feature does not map to a CaptainUp snapshot widget";
	}

	return `[WidgetDock] vendor-driven HitAreaWidget snapshots are intentionally stubbed until CaptainUp ships the agreed snapshot payload contract for '${activeSnapshotWidgetId}'`;
}

function isSameDockState(
	current: DockSurfaceState | null,
	next: DockSurfaceState | null
): boolean {
	if (current === next) {
		return true;
	}

	if (!current || !next) {
		return false;
	}

	return (
		current.hasArea === next.hasArea &&
		current.visible === next.visible &&
		current.enabled === next.enabled &&
		current.parked === next.parked &&
		current.visibleHost === next.visibleHost &&
		current.parkingHost === next.parkingHost
	);
}

export function WidgetDock({
	freeAreas,
	platform,
	orientation,
	children,
	onDockStateChange,
	hitAreaName,
	onActivate,
	hitAreaAreaIds,
	provider,
	activeFeatureId,
	activeSnapshotWidgetId,
	activeDockHasContent,
}: WidgetDockProps) {
	const {
		onHideAllFreeAreas,
		onShowAllFreeAreas,
		onDisableAllFreeAreas,
		onEnableAllFreeAreas,
		sendUiSnapshotError,
		sendSetHitAreasInteractivity,
	} = useCommunicator();

	const {
		snapshotMode,
		snapshotRequests,
		consumeSnapshotRequest,
		forceAllHitAreaWidgetSnapshotsToken,
	} = useSnapshotBridge();

	const [visible, setVisible] = useState(true);
	const [enabled, setEnabled] = useState(true);

	const [visibleHost, setVisibleHost] = useState<HTMLDivElement | null>(null);
	const [parkingHost, setParkingHost] = useState<HTMLDivElement | null>(null);

	const lastHandledForceAllSnapshotsTokenRef = useRef(0);
	const lastEmittedDockStateRef = useRef<DockSurfaceState | null>(null);

	const setVisibleHostRef = useCallback((node: HTMLDivElement | null) => {
		debugLog("[WidgetDock] visible host ref changed", {
			previousPresent: !!visibleHost,
			nextPresent: !!node,
			sameNode: visibleHost === node,
		});
		setVisibleHost(node);
	}, [visibleHost]);

	const setParkingHostRef = useCallback((node: HTMLDivElement | null) => {
		debugLog("[WidgetDock] parking host ref changed", {
			previousPresent: !!parkingHost,
			nextPresent: !!node,
			sameNode: parkingHost === node,
		});
		setParkingHost(node);
	}, [parkingHost]);

	const selectedArea: Readonly<FreeArea> | null = useMemo(() => {
		return resolveSelectedArea(freeAreas, platform, orientation);
	}, [freeAreas, platform, orientation]);

	const hasArea = !!selectedArea;

	const resolvedHitAreaAreaIds = useMemo(() => {
		if (hitAreaAreaIds && hitAreaAreaIds.length > 0) {
			const normalizedAreaIds = hitAreaAreaIds
				.map((areaId) => String(areaId).trim())
				.filter((areaId) => areaId.length > 0);

			debugLog("[WidgetDock] resolved hitAreaAreaIds from props", {
				hitAreaAreaIds: normalizedAreaIds,
			});

			return normalizedAreaIds;
		}

		const fallbackAreaIds = selectedArea ? [selectedArea.id] : [];

		debugLog("[WidgetDock] resolved hitAreaAreaIds from selectedArea", {
			selectedAreaId: selectedArea?.id ?? null,
			hitAreaAreaIds: fallbackAreaIds,
		});

		return fallbackAreaIds;
	}, [hitAreaAreaIds, selectedArea]);

	const dockScale = useMemo(() => {
		if (!selectedArea) {
			return 1;
		}

		const scaleX =
			(selectedArea.width ?? CAPTAINUP_BASE_SIZE) / CAPTAINUP_BASE_SIZE;
		const scaleY =
			(selectedArea.height ?? CAPTAINUP_BASE_SIZE) / CAPTAINUP_BASE_SIZE;
		const nextScale = Math.min(scaleX, scaleY);

		if (!Number.isFinite(nextScale) || nextScale <= 0) {
			debugWarn("[WidgetDock] invalid dockScale calculated, falling back to 1", {
				areaId: selectedArea.id,
				scaleX,
				scaleY,
			});
			return 1;
		}

		return nextScale;
	}, [selectedArea]);

	useEffect(() => {
		if (!provider || !selectedArea) {
			return;
		}

		debugLog("[WidgetDock] sending tooltip position to provider", {
			areaId: selectedArea.id,
			platform,
			orientation,
			tooltipPosition: selectedArea.tooltipPosition,
		});

		provider.setTooltipPosition(selectedArea.tooltipPosition!);
	}, [orientation, platform, provider, selectedArea]);

	useEffect(() => {
		if (!provider) {
			return;
		}

		debugLog("[WidgetDock] vendor snapshot integration is running in stub mode", {
			activeFeatureId,
			activeSnapshotWidgetId,
			hasProvider: true,
		});
	}, [provider, activeFeatureId, activeSnapshotWidgetId]);

	useSubscription(
		onHideAllFreeAreas,
		() => {
			debugLog("[WidgetDock] host -> HideAllFreeAreas");
			setVisible(false);
		},
		[]
	);

	useSubscription(
		onShowAllFreeAreas,
		() => {
			debugLog("[WidgetDock] host -> ShowAllFreeAreas");
			setVisible(true);
		},
		[]
	);

	useSubscription(
		onDisableAllFreeAreas,
		() => {
			debugLog("[WidgetDock] host -> DisableAllFreeAreas");
			setEnabled(false);
		},
		[]
	);

	useSubscription(
		onEnableAllFreeAreas,
		() => {
			debugLog("[WidgetDock] host -> EnableAllFreeAreas");
			setEnabled(true);
		},
		[]
	);

	const parked = !hasArea || !visible || !visibleHost;
	const hasActivation = typeof onActivate === "function";
	const providerHitAreasEnabled = hasActivation && activeDockHasContent;
	const hitAreaInteractive =
		providerHitAreasEnabled &&
		visible &&
		enabled &&
		hasArea;

	useEffect(() => {
		debugLog("[WidgetDock] state", {
			hasArea,
			visible,
			enabled,
			activeDockHasContent,
			hitAreaInteractive,
			parked,
			visibleHostPresent: !!visibleHost,
			parkingHostPresent: !!parkingHost,
			platform,
			orientation,
			freeAreasCount: freeAreas.length,
			freeAreaIds: freeAreas.map((area) => area.id),
			selectedAreaId: selectedArea?.id ?? null,
			selectedArea: selectedArea
				? {
						id: selectedArea.id,
						platform: selectedArea.platform,
						orientation: selectedArea.orientation,
						x: selectedArea.absolute?.x,
						y: selectedArea.absolute?.y,
						width: selectedArea.width,
						height: selectedArea.height,
						tooltipPosition: selectedArea.tooltipPosition,
					}
				: null,
			resolvedHitAreaAreaIds,
			activeFeatureId,
			activeSnapshotWidgetId,
			snapshotMode,
			snapshotRequestsCount: snapshotRequests.length,
			snapshotIntegrationMode: "stubbed_until_vendor_contract",
		});

		const nextDockState: DockSurfaceState = {
			hasArea,
			visible,
			enabled,
			parked,
			visibleHost,
			parkingHost,
		};

		const previousDockState = lastEmittedDockStateRef.current;

		if (isSameDockState(previousDockState, nextDockState)) {
			debugLog("[WidgetDock] onDockStateChange skipped because dock state did not change", {
				previousDockState,
				nextDockState,
			});
			return;
		}

		debugLog("[WidgetDock] onDockStateChange emitting new dock state", {
			previousDockState,
			nextDockState,
			changed: {
				hasArea: previousDockState?.hasArea !== nextDockState.hasArea,
				visible: previousDockState?.visible !== nextDockState.visible,
				enabled: previousDockState?.enabled !== nextDockState.enabled,
				parked: previousDockState?.parked !== nextDockState.parked,
				visibleHost: previousDockState?.visibleHost !== nextDockState.visibleHost,
				parkingHost: previousDockState?.parkingHost !== nextDockState.parkingHost,
			},
		});

		lastEmittedDockStateRef.current = nextDockState;
		onDockStateChange?.(nextDockState);
	}, [
		activeDockHasContent,
		activeFeatureId,
		activeSnapshotWidgetId,
		enabled,
		freeAreas,
		hasArea,
		hitAreaInteractive,
		onDockStateChange,
		orientation,
		parked,
		parkingHost,
		platform,
		resolvedHitAreaAreaIds,
		selectedArea,
		snapshotMode,
		snapshotRequests.length,
		visible,
		visibleHost,
	]);

	useEffect(() => {
		if (!hasActivation) {
			return;
		}

		debugLog("[WidgetDock] sending hitarea interactivity to FW", {
			providerHitAreasEnabled,
			activeDockHasContent,
			activeFeatureId,
		});

		sendSetHitAreasInteractivity({
			enabled: providerHitAreasEnabled,
		});
	}, [
		activeDockHasContent,
		activeFeatureId,
		hasActivation,
		providerHitAreasEnabled,
		sendSetHitAreasInteractivity,
	]);

	useEffect(() => {
		const relevantRequests = snapshotRequests.filter((request) => {
			if (request.surface !== "HitAreaWidget") {
				return false;
			}

			return resolvedHitAreaAreaIds.includes(request.surfaceId);
		});

		if (relevantRequests.length === 0) {
			return;
		}

		const message = buildSnapshotStubMessage(activeSnapshotWidgetId);

		for (const request of relevantRequests) {
			debugWarn("[WidgetDock] snapshot request answered with stub SnapshotError", {
				surfaceId: request.surfaceId,
				reason: request.reason ?? null,
				activeFeatureId,
				activeSnapshotWidgetId,
				message,
			});

			sendUiSnapshotError({
				surface: "HitAreaWidget",
				surfaceId: request.surfaceId,
				message,
			});

			consumeSnapshotRequest(request.token);
		}
	}, [
		activeFeatureId,
		activeSnapshotWidgetId,
		consumeSnapshotRequest,
		resolvedHitAreaAreaIds,
		sendUiSnapshotError,
		snapshotRequests,
	]);

	useEffect(() => {
		if (
			forceAllHitAreaWidgetSnapshotsToken <= 0 ||
			lastHandledForceAllSnapshotsTokenRef.current ===
				forceAllHitAreaWidgetSnapshotsToken
		) {
			return;
		}

		lastHandledForceAllSnapshotsTokenRef.current =
			forceAllHitAreaWidgetSnapshotsToken;

		if (resolvedHitAreaAreaIds.length === 0) {
			return;
		}

		const message = buildSnapshotStubMessage(activeSnapshotWidgetId);

		debugWarn("[WidgetDock] forced snapshot resend answered with stub SnapshotError", {
			activeFeatureId,
			activeSnapshotWidgetId,
			areaIds: resolvedHitAreaAreaIds,
			message,
		});

		for (const areaId of resolvedHitAreaAreaIds) {
			sendUiSnapshotError({
				surface: "HitAreaWidget",
				surfaceId: areaId,
				message,
			});
		}
	}, [
		activeFeatureId,
		activeSnapshotWidgetId,
		forceAllHitAreaWidgetSnapshotsToken,
		resolvedHitAreaAreaIds,
		sendUiSnapshotError,
	]);

	if (hasActivation && (!hitAreaName || hitAreaName.trim().length === 0)) {
		throw new Error("[WidgetDock] hitAreaName must be provided when onActivate is set");
	}

	const dockContentWrapperStyle: React.CSSProperties = {
		width: CAPTAINUP_BASE_SIZE,
		height: CAPTAINUP_BASE_SIZE,
		transform: `scale(${dockScale})`,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	};

	return (
		<>
			<div ref={setParkingHostRef} style={OFFSCREEN_PARK_STYLE} aria-hidden="true" />

			{selectedArea ? (
				<div
					ref={setVisibleHostRef}
					className="fixed z-[2]"
					style={{
						left: selectedArea.absolute?.x,
						top: selectedArea.absolute?.y,
						width: selectedArea.width,
						height: selectedArea.height,
						display: visible ? "block" : "none",
						pointerEvents: "auto",
					}}
					aria-label="WidgetDock"
					data-efw-dock-area-id={selectedArea.id}
					data-efw-dock-platform={selectedArea.platform}
					data-efw-dock-orientation={selectedArea.orientation}
					data-efw-dock-tooltip-position={selectedArea.tooltipPosition}
				>
					{hasActivation ? (
						<HitAreaWidget
							name={hitAreaName!}
							freeAreas={freeAreas}
							platform={platform}
							orientation={orientation}
							areaIds={resolvedHitAreaAreaIds}
							visible={visible && hasArea}
							enabled={hitAreaInteractive}
							onActivate={onActivate!}
						>
							<div className="flex h-full w-full items-center justify-center">
								<div style={dockContentWrapperStyle}>{children}</div>
							</div>
						</HitAreaWidget>
					) : (
						<div className="flex h-full w-full items-center justify-center">
							<div style={dockContentWrapperStyle}>{children}</div>
						</div>
					)}
				</div>
			) : null}
		</>
	);
}

export default WidgetDock;
